import React, { useEffect, useState } from "react";
import { FileText, Upload, Trash2, ExternalLink, Loader2, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MAX_BYTES = 25 * 1024 * 1024; // 25 MB

export default function InstituteMandatoryDisclosure() {
  const { role } = useAuth();
  const canEdit = role === "institute_admin";

  const [doc, setDoc] = useState({ url: "", public_id: "", filename: "" });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institute/mandatory-disclosure");
      setDoc({
        url: data?.url || "",
        public_id: data?.public_id || "",
        filename: data?.filename || "",
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/institute/mandatory-disclosure");
        if (cancelled) return;
        setDoc({
          url: data?.url || "",
          public_id: data?.public_id || "",
          filename: data?.filename || "",
        });
      } catch (err) {
        if (!cancelled) toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be re-selected
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("File too large. Maximum size is 25 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("kind", "mandatory_disclosure");
      fd.append("file", file);
      const { data: up } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const payload = {
        url: up.url,
        public_id: up.public_id,
        filename: file.name,
      };
      await api.put("/institute/mandatory-disclosure", payload);
      setDoc(payload);
      toast.success("Mandatory disclosure uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete("/institute/mandatory-disclosure");
      setDoc({ url: "", public_id: "", filename: "" });
      toast.success("Mandatory disclosure removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    } finally {
      setConfirmDelete(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="i-mandatory-disclosure-page">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#234333]/10 text-[#234333] flex items-center justify-center shrink-0">
          <ShieldCheck size={20} strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <h1 className="font-serif text-2xl text-[#1c1a17]">Mandatory Disclosure</h1>
          <p className="text-sm text-[#6b665e] mt-1">
            Upload the institute&apos;s Mandatory Disclosure PDF (AICTE / regulator format).
            Maximum 25 MB. Visible publicly on your institute website next to the
            Student ERP button.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-md shadow-sm border border-[#e5e0d8] p-6">
        {loading ? (
          <div className="flex items-center gap-2 text-[#6b665e] text-sm">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : doc.url ? (
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-md bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
              <FileText size={26} strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#1c1a17] truncate" data-testid="i-mandatory-filename">
                {doc.filename || "Mandatory Disclosure.pdf"}
              </div>
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="i-mandatory-view-link"
                className="inline-flex items-center gap-1 mt-1 text-xs text-[#234333] hover:underline"
              >
                <ExternalLink size={11} strokeWidth={1.8} />
                View / Download
              </a>
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                <label
                  htmlFor="md-replace"
                  data-testid="i-mandatory-replace-btn"
                  className={`inline-flex items-center gap-1.5 cursor-pointer bg-[#234333] hover:bg-[#1a3326] text-white text-xs font-medium px-3 py-2 rounded-sm ${
                    uploading ? "opacity-60 pointer-events-none" : ""
                  }`}
                >
                  {uploading ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Upload size={12} strokeWidth={1.8} />
                  )}
                  Replace
                </label>
                <input
                  id="md-replace"
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={onFile}
                  data-testid="i-mandatory-replace-input"
                />
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  data-testid="i-mandatory-delete-btn"
                  className="inline-flex items-center gap-1.5 bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 text-xs font-medium px-3 py-2 rounded-sm"
                >
                  <Trash2 size={12} strokeWidth={1.8} />
                  Remove
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 border-2 border-dashed border-[#e5e0d8] rounded-md">
            <FileText size={28} strokeWidth={1.2} className="mx-auto text-[#a8a298]" />
            <div className="text-sm text-[#6b665e] mt-2">
              No mandatory disclosure uploaded yet.
            </div>
            {canEdit && (
              <>
                <label
                  htmlFor="md-upload"
                  data-testid="i-mandatory-upload-btn"
                  className={`mt-4 inline-flex items-center gap-1.5 cursor-pointer bg-[#234333] hover:bg-[#1a3326] text-white text-xs font-medium px-4 py-2.5 rounded-sm ${
                    uploading ? "opacity-60 pointer-events-none" : ""
                  }`}
                >
                  {uploading ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Upload size={12} strokeWidth={1.8} />
                  )}
                  Upload PDF (max 25 MB)
                </label>
                <input
                  id="md-upload"
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={onFile}
                  data-testid="i-mandatory-upload-input"
                />
              </>
            )}
          </div>
        )}

        {!canEdit && (
          <div className="mt-4 text-[11px] text-[#a8a298]">
            Only Institute Admins can upload or remove this document.
          </div>
        )}
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove mandatory disclosure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the uploaded PDF and hide the public link from your institute website. You can re-upload at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="i-mandatory-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="i-mandatory-delete-confirm"
              className="bg-rose-600 hover:bg-rose-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
